import { createContext, useContext, useMemo, ReactNode } from 'react'
import { ISkillsStandards, ILvl4Item } from 'pages/NewProject/components/ProjectSkillsStandards/interfaces'
import { useBrowseNavigation } from '../hooks/useBrowseNavigation'
import { useSelectionCounts } from '../hooks/useSelectionCounts'
import { BrowseTab, NavigationPathItem, TreeItem, BrowseTreeData } from '../types'

interface BrowseTreeContextValue {
  activeTab: BrowseTab
  setActiveTab: (tab: BrowseTab) => void
  currentLevel: 1 | 2 | 3 | 4
  navigationPath: NavigationPathItem[]
  currentItems: TreeItem[]
  isDataReady: boolean
  navigateForward: (item: TreeItem) => void
  navigateBack: () => void
  navigateToBreadcrumb: (level: number) => void
  pendingIds: Set<string>
  alreadyAddedIds: Set<string>
  onToggle: (item: ILvl4Item) => void
  getSelectionCount: (itemId: string) => number
}

interface BrowseTreeProviderProps {
  skillsStandards: ISkillsStandards | null
  pendingIds: Set<string>
  alreadyAddedIds: Set<string>
  onToggle: (item: ILvl4Item) => void
  children: ReactNode
}

const BrowseTreeContext = createContext<BrowseTreeContextValue | undefined>(undefined)
BrowseTreeContext.displayName = 'BrowseTreeContext'

export const BrowseTreeProvider = ({
  skillsStandards,
  pendingIds,
  alreadyAddedIds,
  onToggle,
  children,
}: BrowseTreeProviderProps) => {
  const treeData = useMemo((): BrowseTreeData | null => {
    if (!skillsStandards?.curriculumItems) return null
    return {
      subjects: skillsStandards.curriculumItems.subjects ?? [],
      axes: skillsStandards.curriculumItems.axes ?? [],
    }
  }, [skillsStandards])

  const isDataReady = treeData !== null
  const navigation = useBrowseNavigation(treeData, isDataReady)

  const countMap = useSelectionCounts(
    treeData?.subjects ?? [],
    treeData?.axes ?? [],
    pendingIds,
    navigation.activeTab,
  )

  const getSelectionCount = useMemo(() => {
    return (itemId: string): number => Math.max(0, countMap.get(itemId) ?? 0)
  }, [countMap])

  const value = useMemo((): BrowseTreeContextValue => ({
    activeTab: navigation.activeTab,
    setActiveTab: navigation.setActiveTab,
    currentLevel: navigation.currentLevel,
    navigationPath: navigation.navigationPath,
    currentItems: navigation.currentItems,
    isDataReady,
    navigateForward: navigation.navigateForward,
    navigateBack: navigation.navigateBack,
    navigateToBreadcrumb: navigation.navigateToBreadcrumb,
    pendingIds,
    alreadyAddedIds,
    onToggle,
    getSelectionCount,
  }), [
    navigation,
    isDataReady,
    pendingIds,
    alreadyAddedIds,
    onToggle,
    getSelectionCount,
  ])

  return (
    <BrowseTreeContext.Provider value={value}>
      {children}
    </BrowseTreeContext.Provider>
  )
}

export const useBrowseTree = (): BrowseTreeContextValue => {
  const context = useContext(BrowseTreeContext)
  if (!context) {
    throw new Error('useBrowseTree must be used within a BrowseTreeProvider')
  }
  return context
}
